<template>
    <div id="order-audit-details">
        <component :is="wrapperComponent" v-bind="wrapperProps">
            <h5 v-if="order && order.auditId" class="text-success">Complete</h5>

            <h5>
                <router-link :to="patientDashboardRoute" target="_blank">
                    {{ this.order.patient.lastName }}, {{ this.order.patient.firstName }}
                    <small
                        ><small><i class="fas fa-external-link-alt" /></small
                    ></small>
                </router-link>
            </h5>
            <h6>
                Order Id:
                <router-link :to="orderDetailsRoute" target="_blank">
                    {{ this.order.id }} <small><i class="fas fa-external-link-alt" /></small>
                </router-link>
            </h6>
            <b-row id="order-authorization-attestation-row">
                <b-col class="text-center" cols="6">
                    {{ this.orderAuthorization.attestationLanguage }}
                    <div v-html="signatureHtml" />
                </b-col>
                <b-col cols="6">
                    <h5 id="total-row">Amount Due: {{ amountOwed | currency }}</h5>
                    <h5>Payments:<span v-show="paymentItems.length == 0"> None</span></h5>
                    <b-row id="payments-row" v-show="paymentItems.length > 0">
                        <b-col>
                            <b-table :items="paymentItems" :fields="paymentFields"> </b-table>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>

            <h5>Prescriptions:</h5>
            <b-row id="prescription-rows">
                <b-col>
                    <b-table :items="scriptItems" :fields="scriptFields">
                        <template #cell(rxID)="data">
                            <router-link :to="scriptDetailsRoute(data.item)">
                                {{ data.value }}
                            </router-link>
                        </template>
                        <template #cell(payment)="data">
                            <div
                                :key="claim.id"
                                v-for="claim in data.item.claims.filter(
                                    (claim) => claim.reversed == null
                                )"
                            >
                                <strong :class="transactionStatusClass(claim.transactionStatus)"
                                    >{{ claim.planName }}:</strong
                                >
                                {{ (claim.planPaid + claim.voucherPaid) | currency }}
                            </div>
                            <strong>Cost:</strong> {{ data.item.cost | currency }}<br />
                            <strong>Tax:</strong> {{ data.item.tax | currency }}<br />
                            <div :class="marginClass(data.item)">
                                <strong>Margin:</strong> {{ margin(data.item) | currency }}
                            </div>
                        </template>
                    </b-table>
                </b-col>
            </b-row>

            <b-row id="buttons-row" v-if="showCompleteButton">
                <b-col>
                    <b-button @click="completeClicked" variant="success">Complete</b-button>
                </b-col>
            </b-row>
        </component>
    </div>
</template>

<script lang="ts">
import Axios from "axios";
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { Order } from "@/models/Order";
import { BvTableFieldArray } from "bootstrap-vue";
import moment from "moment";
import { ClaimRejects } from "@/data/ClaimRejects";
import { Prescription } from "@/models/Prescription";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { OrderAuthorization } from "@/models/OrderAuthorization";

@Component({
    name: "OrderAuditDetails",
})
export default class OrderAuditDetails extends Vue {
    @Prop({ default: false }) queueMode!: boolean;
    @Prop({ default: 0 }) orderId!: number;

    private order: Order = new Order();
    private orderAuthorization: OrderAuthorization = new OrderAuthorization();
    private rejects: Array<{ Code: string; Value: string }> = ClaimRejects.codes;
    private currencyFormatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    });

    transactionStatusClass(status: string) {
        return status == "Rejected" ? "text-danger" : status == "Paid" ? "text-success" : "";
    }

    rejectData(codeList: Array<string>) {
        console.log("rejectData: ", codeList);
        return codeList
            .filter((rejectcode) => rejectcode.trim() != "")
            .map((rejectCode: any) => {
                return (
                    this.rejects.find((r) => r.Code == rejectCode.trim()) ??
                    this.rejects.find((r) => r.Code == parseInt(rejectCode, 10).toString()) ?? {
                        Code: rejectCode,
                        Value: "Contact IT.",
                    }
                );
            });
    }

    get amountOwed() {
        const scriptTotal = this.order.prescriptions.reduce((total, rx) => {
            return total + (rx.patientPay ?? 0);
        }, 0);
        const addonTotal = this.order.items.reduce((grandTotal, item) => {
            const price = item.price;
            const qty = item.quantity;
            const tax = item.tax ? +item.tax : 0;
            const itemTotal = price * qty + tax;
            return grandTotal + itemTotal;
        }, 0);
        const balance = scriptTotal + addonTotal;
        return this.currencyFormatter.format(balance);
    }

    get rejectFields(): BvTableFieldArray {
        // return [];
        return [
            { key: "Code", label: "" },
            { key: "Value", label: "" },
        ];
    }

    get patientDashboardRoute() {
        return {
            name: "PatientDashboard",
            params: {
                id: this.order.patientID,
            },
        };
    }

    margin(rx: Prescription) {
        var paidClaims = rx.claims.filter((claim) => claim.reversed == null);
        var insTotal = paidClaims.reduce(
            (total, claim) => (total += claim.planPaid + claim.voucherPaid),
            0
        );
        return insTotal + (rx.patientPay ?? 0) - ((rx.cost ?? 0) + (rx.tax ?? 0));
    }

    marginClass(rx: Prescription) {
        return this.margin(rx) >= 0 ? "" : "text-danger";
    }

    get orderDetailsRoute() {
        return {
            name: "OrderDetails",
            params: {
                orderId: this.order.id,
            },
        };
    }

    scriptDetailsRoute(rxRow: Prescription) {
        return {
            name: "PrescriptionDetails",
            params: {
                storeID: rxRow.storeID,
                rxNumber: rxRow.rxNumber,
                rfNumber: rxRow.rfNumber!,
            },
        };
    }

    get scriptItems() {
        return this.order?.prescriptions ?? [];
    }

    get scriptFields(): BvTableFieldArray {
        return [
            { key: "rxID", headerTitle: "Full Prescription Identifier" },
            { key: "drugName", label: "Drug" },
            { key: "fillDate", formatter: (d) => moment(d).format("L") },
            { key: "programName", label: "Program" },
            { key: "storeName", label: "Store" },
            { key: "patientPay", formatter: this.currencyFormatter.format },
            { key: "payment", label: "Price" },
        ];
    }

    get paymentItems() {
        return this.order?.payments ?? [];
    }

    get paymentFields(): BvTableFieldArray {
        // return [];
        return [
            { key: "authorizedAmount", formatter: this.currencyFormatter.format },
            { key: "created", formatter: (d: any) => moment.utc(d).local().format("L LT") },
            { key: "createdBy" },
        ];
    }

    get wrapperComponent() {
        return this.queueMode ? "div" : "card";
    }

    get wrapperProps() {
        return this.queueMode ? {} : { title: "Order Audit" };
    }

    get showCompleteButton() {
        if (!this.order || !this.order.id) return false;
        // In queue mode, don't show button if already completed
        if (this.queueMode && this.order.auditId) return false;
        return true;
    }

    get routeAuditId(): number {
        if (this.queueMode && this.orderId) {
            return this.orderId;
        }
        const res = parseInt(this.$route.params.id);
        return isNaN(res) ? 0 : res;
    }

    @Watch('orderId')
    async onOrderIdChanged() {
        if (this.queueMode && this.orderId) {
            await this.loadOrderData(this.orderId);
        }
    }

    async created() {
        if (this.routeAuditId) {
            await this.loadOrderData(this.routeAuditId);
        }
    }

    async loadOrderData(id: number) {
        try {
            let resp = await Axios.get<Order>(`/Order/Audit/${id}`);
            this.order = new Order(resp.data);

            let orderAuthResp = await Axios.get<OrderAuthorization>(
                `/Order/Authorization/${id}`
            );
            this.orderAuthorization = new OrderAuthorization(orderAuthResp.data);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    get signatureHtml() {
        const sigData = this.orderAuthorization.signatureData;
        if(!sigData){
            return "";
        }
        if (sigData != null && sigData.startsWith("<")) {
            return sigData;
        }
        if (sigData.startsWith("data")) {
            return `<img src="${sigData}" alt="Signature"/>`;
        }
        return `<img src="data:image/png;base64,${sigData}" alt="Signature"/>`;
    }

    async completeClicked() {
        if (this.order && this.order.id) {
            try {
                const resp = await Axios.post<Order>(`/Order/Audit/${this.order.id}/Complete`);
                this.order = new Order(resp.data);
                this.$notification(NotificationOptions.successSaveNotificationPreset("OrderAudit"));
                
                if (this.queueMode) {
                    // Emit completion event for queue processing
                    this.$emit('completed', this.order);
                } else {
                    // Navigate back to queue page in standalone mode
                    this.$router.replace({ name: 'OrderAuditQueue' });
                }
            } catch (e) {
                this.$notification(NotificationOptions.error(e));
            }
        }
    }
}
</script>

<style scoped>
</style>
